import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Divider,
  alpha,
  useTheme,
  CircularProgress,
  Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { createRuns } from "../../api/services/scheduling";
import { useSnackbar } from "notistack";
import { useQueryClient } from "react-query";
import { QK } from "../../base/qk";
import CustomInput from "../../components/FormFields/CustomInput";
import CustomSelect from "../../components/FormFields/CustomSelect/CustomSelect";

const CreateRunModal = ({ show, handleClose, templateId, area }) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [runName, setRunName] = useState("");
  const [runType, setRunType] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const runTypeOptions = [
    { value: "am", label: "AM" },
    { value: "pm", label: "PM" },
  ];

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!runName || !runType) {
      enqueueSnackbar("Please fill all the fields", { variant: "error" });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await createRuns({
        name: runName,
        template: templateId,
        sublocation: area,
        run_type: runType,
      });

      if (response.status === 201) {
        queryClient.invalidateQueries([QK.runs]);
        queryClient.invalidateQueries([QK.runsByArea]);
        queryClient.invalidateQueries([QK.scheduledRunVisits]);
        enqueueSnackbar("Run created successfully", { variant: "success" });
        setRunName("");
        setRunType("");
        handleClose();
      }
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || "Failed to create run", { variant: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRunNameChange = (e) => {
    setRunName(e.target.value);
  };

  const handleRunTypeChange = (e) => {
    setRunType(e.target.value);
  };

  const handleModalClose = () => {
    setRunName("");
    setRunType("");
    handleClose();
  };

  return (
    <Dialog
      open={show}
      onClose={handleModalClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow: "0px 24px 48px -12px rgba(16, 24, 40, 0.25)",
        },
      }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2.5,
            pb: 1.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "10px",
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <AddCircleOutlineIcon sx={{ color: theme.palette.primary.main, fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={600} sx={{ color: "#1A1A2E" }}>
                Create New Run
              </Typography>
              <Typography variant="caption" sx={{ color: "#6B7280" }}>
                Add a new run for scheduling
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleModalClose}
            size="small"
            sx={{
              color: "#9CA3AF",
              "&:hover": { bgcolor: "#F3F4F6", color: "#4B5563" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ opacity: 0.4 }} />
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ p: 2.5, pt: 2 }}>
          <Stack spacing={2.5}>
            <CustomInput
              label="Run Name"
              placeholder="Enter the run name"
              value={runName}
              onChange={handleRunNameChange}
              fullWidth
              required
              size="medium"
            />

            <CustomSelect
              options={runTypeOptions}
              label="Run Type"
              name="run_type"
              value={runType}
              onChange={handleRunTypeChange}
              fullWidth
              placeholder="Select Run Type"
              required
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2.5, pt: 0, gap: 1.5 }}>
          <Button
            onClick={handleModalClose}
            variant="outlined"
            disabled={isSubmitting}
            sx={{
              textTransform: "none",
              fontWeight: 500,
              borderRadius: "10px",
              borderColor: "#D0D5DD",
              color: "#344054",
              px: 3,
              "&:hover": {
                bgcolor: "#F9FAFB",
                borderColor: "#98A2B3",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "10px",
              bgcolor: "#2469F0",
              px: 4,
              "&:hover": {
                bgcolor: "#1A5BD4",
                boxShadow: "0px 4px 12px rgba(36, 105, 240, 0.3)",
              },
            }}
          >
            {isSubmitting ? "Creating..." : "Create Run"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateRunModal;